/* Snack-Finder-Quiz – rein clientseitig, Matching über Produkt-Tags.

   Zwei Dinge verhindern hier leere Ergebnisseiten:

   1. Optionen, die zusammen mit den bisherigen Antworten kein einziges Produkt
      übrig lassen, werden deaktiviert. Der Nutzer kann eine Sackgasse also gar
      nicht erst auswählen. Das Sortiment hat naturgemäss Lücken – es gibt zum
      Beispiel keinen Kauartikel für Katzen – und die soll der Finder zeigen,
      statt den Nutzer hineinlaufen zu lassen.
   2. Falls doch einmal nichts übrig bleibt (etwa bei einer Mehrfachauswahl,
      die erst in Kombination leer läuft), werden die Einschränkungen von hinten
      nach vorn geloest, bis wieder Treffer da sind. Die erste Frage (Hund oder
      Katze) faellt dabei zuletzt – ein Kauartikel für Hunde als Vorschlag für
      eine Katze waere schlechter als gar kein Vorschlag. */
const { formatMoney } = window.themeHelpers;

class SnackQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.results = this.querySelector('[data-quiz-results]');
    this.progress = this.querySelector('[data-quiz-progress]');
    this.backButton = this.querySelector('[data-quiz-back]');
    this.nextButton = this.querySelector('[data-quiz-next]');
    this.approxNote = this.querySelector('[data-quiz-approx]');
    try {
      this.products = JSON.parse(this.querySelector('[data-quiz-products]').textContent);
    } catch (e) {
      this.products = [];
    }
    if (!this.steps.length) return;

    this.index = 0;
    this.progress.innerHTML = this.steps
      .map(() => '<span class="quiz__progress-step"></span>')
      .join('');

    this.addEventListener('click', (e) => {
      const option = e.target.closest('[data-quiz-option]');
      if (option && !option.disabled) this.selectOption(option);
      if (e.target.closest('[data-quiz-back]')) this.go(-1);
      if (e.target.closest('[data-quiz-next]')) this.go(1);
      if (e.target.closest('[data-quiz-restart]')) this.restart();
    });

    this.show(0);
  }

  get currentStep() {
    return this.steps[this.index];
  }

  optionTags(option) {
    return (option.dataset.tags || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
  }

  groupsFrom(steps) {
    const groups = [];
    steps.forEach((step) => {
      step.querySelectorAll('[data-quiz-option].is-selected').forEach((option) => {
        const tags = this.optionTags(option);
        if (tags.length) groups.push(tags);
      });
    });
    return groups;
  }

  matching(groups) {
    return this.products.filter((product) => {
      if (!product.available) return false;
      const tags = (product.tags || []).map((t) => t.trim().toLowerCase());
      return groups.every((group) => group.some((tag) => tags.includes(tag)));
    });
  }

  /* Optionen ausgrauen, die zusammen mit den bisherigen Antworten leer laufen. */
  updateAvailability() {
    const prior = this.groupsFrom(this.steps.slice(0, this.index));
    const step = this.currentStep;
    const multi = step.dataset.multi === 'true';
    const selectedHere = multi ? this.groupsFrom([step]) : [];

    step.querySelectorAll('[data-quiz-option]').forEach((option) => {
      const tags = this.optionTags(option);
      if (!tags.length || option.classList.contains('is-selected')) {
        option.disabled = false;
        option.classList.remove('is-unavailable');
        option.removeAttribute('aria-disabled');
        return;
      }
      const possible = this.matching(prior.concat(selectedHere, [tags])).length > 0;
      option.disabled = !possible;
      option.classList.toggle('is-unavailable', !possible);
      if (possible) {
        option.removeAttribute('aria-disabled');
      } else {
        option.setAttribute('aria-disabled', 'true');
      }
    });
  }

  selectOption(option) {
    const step = option.closest('[data-quiz-step]');
    const multi = step.dataset.multi === 'true';
    if (multi) {
      option.classList.toggle('is-selected');
      option.setAttribute('aria-pressed', option.classList.contains('is-selected'));
      this.updateAvailability();
    } else {
      step.querySelectorAll('[data-quiz-option]').forEach((o) => {
        o.classList.toggle('is-selected', o === option);
        o.setAttribute('aria-pressed', o === option ? 'true' : 'false');
      });
      setTimeout(() => this.go(1), 220);
    }
  }

  go(dir) {
    const next = this.index + dir;
    if (next < 0) return;
    if (next >= this.steps.length) {
      this.showResults();
      return;
    }
    this.show(next);
  }

  show(index) {
    this.index = index;
    this.steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
    this.results.classList.remove('is-active');
    this.progress.querySelectorAll('.quiz__progress-step').forEach((seg, i) => {
      seg.classList.toggle('is-done', i <= index);
    });
    this.backButton.hidden = index === 0;
    const multi = this.currentStep.dataset.multi === 'true';
    this.nextButton.hidden = !multi;
    this.updateAvailability();
    this.currentStep.querySelector('[data-quiz-option]:not([disabled])')?.focus();
  }

  collectConstraints() {
    /* Pro gewaehlter Option eine Tag-Gruppe. Innerhalb einer Gruppe ODER,
       zwischen den Gruppen UND. Reihenfolge = Reihenfolge der Fragen. */
    return this.groupsFrom(this.steps);
  }

  showResults() {
    const constraints = this.collectConstraints();
    const limit = parseInt(this.dataset.resultsLimit || 6, 10);

    let used = constraints.slice();
    let matches = this.matching(used);
    let approximate = false;
    while (!matches.length && used.length) {
      used = used.slice(0, -1);
      matches = this.matching(used);
      approximate = true;
    }

    const grid = this.querySelector('[data-quiz-results-grid]');
    const empty = this.querySelector('[data-quiz-results-empty]');
    if (this.approxNote) this.approxNote.hidden = !(approximate && matches.length);

    if (!matches.length) {
      empty.hidden = false;
      grid.innerHTML = '';
    } else {
      empty.hidden = true;
      grid.innerHTML = matches.slice(0, limit).map((p) => `
        <div role="listitem">
          <article class="product-card">
            <div class="product-card__media" style="aspect-ratio: 1;">
              ${p.image ? `<img class="product-card__img product-card__img--main" src="${p.image}" alt="" width="540" height="540" loading="lazy">` : ''}
            </div>
            <div class="product-card__content">
              <h3 class="product-card__title"><a href="${p.url}">${p.title}</a></h3>
              <div class="product-card__price"><div class="price"><span class="price__current">${formatMoney(p.price)}</span></div></div>
            </div>
          </article>
        </div>`).join('');
    }

    this.steps.forEach((step) => step.classList.remove('is-active'));
    this.backButton.hidden = true;
    this.nextButton.hidden = true;
    this.progress.querySelectorAll('.quiz__progress-step').forEach((seg) => seg.classList.add('is-done'));
    this.results.classList.add('is-active');
    this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  restart() {
    this.querySelectorAll('[data-quiz-option]').forEach((o) => {
      o.classList.remove('is-selected');
      o.setAttribute('aria-pressed', 'false');
    });
    if (this.approxNote) this.approxNote.hidden = true;
    this.show(0);
  }
}
if (!customElements.get('snack-quiz')) customElements.define('snack-quiz', SnackQuiz);
